// Text-emissive ray effect: treat the text itself as a light emitter.
// The text's own color becomes the light color; rays emanate outward into background.

export function renderRaylight(sourceCanvas, options = {}, sharedGl) {
  const width = Math.max(1, sourceCanvas.width | 0);
  const height = Math.max(1, sourceCanvas.height | 0);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const gl =
    sharedGl ||
    canvas.getContext('webgl', { premultipliedAlpha: true }) ||
    canvas.getContext('experimental-webgl');
  if (!gl) return sourceCanvas;

  const {
    samples = 72, // steps per direction (max 256)
    directions = 16, // number of ray directions (max 64)
    stepPx = 2.0, // step size in pixels per iteration
    decay = 0.96, // falloff per step
    weight = 0.075, // per-step contribution
    exposure = 1.0, // final multiplier
    intensity = 1.0, // global intensity 0..1
    threshold = 0.08, // alpha threshold to consider text present
    onlyRays = false, // output only rays (with derived alpha)
    blend = 'screen', // 'screen' | 'add'
  } = options || {};

  const vs = `
        attribute vec2 a_position;
        attribute vec2 a_texCoord;
        varying vec2 v_uv;
        void main(){ v_uv = a_texCoord; gl_Position = vec4(a_position,0.0,1.0); }
    `;

  const fs = `
        precision mediump float;
        varying vec2 v_uv;
        uniform sampler2D u_src;
        uniform vec2 u_texelSize;   // 1/width, 1/height
        uniform float u_samples;     // [1..256]
        uniform float u_dirs;        // [1..64]
        uniform float u_stepPx;      // pixels per step
        uniform float u_decay;       // 0..1
        uniform float u_weight;      // contribution per step
        uniform float u_exposure;    // multiplier
        uniform float u_intensity;   // 0..1
        uniform float u_threshold;   // alpha threshold for text
        uniform float u_blendAdd;    // 1 add, 0 screen
        uniform float u_onlyRays;    // 1 rays-only

        #define saturate(x) clamp(x,0.0,1.0)

        vec3 linearToSrgb(const vec3 x){
            return sqrt((-2.8450663e-1*x+1.2580714)*x-2.4727747e-3);
        }

        vec3 screenBlend(vec3 base, vec3 add){
            return 1.0 - (1.0 - base) * (1.0 - add);
        }

        float luma(vec3 c){ return dot(c, vec3(0.299, 0.587, 0.114)); }

        void main(){
            vec4 base = texture2D(u_src, v_uv);
            float baseA = base.a;

            // Accumulate rays by sampling backward along multiple directions to find emissive text.
            vec3 rays = vec3(0.0);
            const int MAX_DIRS = 64;
            const int MAX_SAMPLES = 256;
            float dirCount = max(1.0, min(64.0, u_dirs));
            float sampleCount = max(1.0, min(256.0, u_samples));
            float angleStep = 6.28318530718 / dirCount; // 2*pi
            vec2 stepUVBase = u_texelSize * u_stepPx;

            for (int d = 0; d < MAX_DIRS; d++) {
                if (float(d) >= dirCount) break;
                float angle = float(d) * angleStep;
                vec2 dir = vec2(cos(angle), sin(angle));
                vec2 stepUV = dir * stepUVBase;
                vec2 coord = v_uv;
                float illum = 1.0;
                vec3 dirAccum = vec3(0.0);
                bool hitOnce = false;
                for (int s = 0; s < MAX_SAMPLES; s++) {
                    if (float(s) >= sampleCount) break;
                    // step backwards along direction to search for emissive text
                    coord -= stepUV;
                    if (any(lessThan(coord, vec2(0.0))) || any(greaterThan(coord, vec2(1.0)))) break;
                    vec4 samp = texture2D(u_src, coord);
                    if (samp.a > u_threshold) {
                        // Use the text color at the first hit predominately; keep accumulating softly beyond
                        vec3 emitColor = samp.rgb;
                        dirAccum += emitColor * (illum * u_weight);
                        illum *= u_decay;
                        hitOnce = true;
                    } else if (hitOnce) {
                        // After leaving the glyph, continue dimly to stretch the streak
                        dirAccum += dirAccum * (u_weight * 0.15);
                        illum *= u_decay;
                    }
                }
                rays += dirAccum;
            }

            rays *= u_exposure * u_intensity;
            vec3 raysClamped = clamp(rays, 0.0, 1.0);

            if (u_onlyRays > 0.5) {
                float alphaOut = saturate(luma(raysClamped));
                vec3 outRGB = linearToSrgb(raysClamped);
                gl_FragColor = vec4(outRGB, alphaOut);
            } else {
                vec3 mixed = (u_blendAdd > 0.5)
                    ? (base.rgb + raysClamped)
                    : screenBlend(base.rgb, raysClamped);
                vec3 outRGB = linearToSrgb(mixed);
                gl_FragColor = vec4(outRGB, baseA);
            }
        }
    `;

  const vert = _createShader(gl, gl.VERTEX_SHADER, vs);
  const frag = _createShader(gl, gl.FRAGMENT_SHADER, fs);
  if (!vert || !frag) return sourceCanvas;
  const prog = _createProgram(gl, vert, frag);
  if (!prog) return sourceCanvas;
  gl.useProgram(prog);

  const pos = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, pos);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
    gl.STATIC_DRAW
  );
  const uv = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, uv);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([0, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1, 0]),
    gl.STATIC_DRAW
  );

  const aPos = gl.getAttribLocation(prog, 'a_position');
  const aUv = gl.getAttribLocation(prog, 'a_texCoord');
  gl.bindBuffer(gl.ARRAY_BUFFER, pos);
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindBuffer(gl.ARRAY_BUFFER, uv);
  gl.enableVertexAttribArray(aUv);
  gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 0, 0);

  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sourceCanvas);

  const uSrc = gl.getUniformLocation(prog, 'u_src');
  const uTexel = gl.getUniformLocation(prog, 'u_texelSize');
  const uSamples = gl.getUniformLocation(prog, 'u_samples');
  const uDirs = gl.getUniformLocation(prog, 'u_dirs');
  const uStepPx = gl.getUniformLocation(prog, 'u_stepPx');
  const uDecay = gl.getUniformLocation(prog, 'u_decay');
  const uWeight = gl.getUniformLocation(prog, 'u_weight');
  const uExposure = gl.getUniformLocation(prog, 'u_exposure');
  const uIntensity = gl.getUniformLocation(prog, 'u_intensity');
  const uThresh = gl.getUniformLocation(prog, 'u_threshold');
  const uBlendAdd = gl.getUniformLocation(prog, 'u_blendAdd');
  const uOnly = gl.getUniformLocation(prog, 'u_onlyRays');

  gl.uniform1i(uSrc, 0);
  gl.uniform2f(uTexel, 1 / width, 1 / height);
  gl.uniform1f(uSamples, Math.max(1, Math.min(256, Math.floor(samples))));
  gl.uniform1f(uDirs, Math.max(1, Math.min(64, Math.floor(directions))));
  gl.uniform1f(uStepPx, Math.max(0.25, stepPx));
  gl.uniform1f(uDecay, Math.max(0.0, Math.min(1.0, decay)));
  gl.uniform1f(uWeight, weight);
  gl.uniform1f(uExposure, exposure);
  gl.uniform1f(uIntensity, Math.max(0.0, Math.min(1.0, intensity)));
  gl.uniform1f(uThresh, Math.max(0.0, Math.min(1.0, threshold)));
  gl.uniform1f(uBlendAdd, blend === 'add' ? 1.0 : 0.0);
  gl.uniform1f(uOnly, onlyRays ? 1.0 : 0.0);

  gl.viewport(0, 0, width, height);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.drawArrays(gl.TRIANGLES, 0, 6);

  return sharedGl ? gl.canvas : canvas;
}

function _createShader(gl, type, src) {
  const s = gl.createShader(type);
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.error('TextFX raylight shader error:', gl.getShaderInfoLog(s));
    gl.deleteShader(s);
    return null;
  }
  return s;
}
function _createProgram(gl, vs, fs) {
  const p = gl.createProgram();
  gl.attachShader(p, vs);
  gl.attachShader(p, fs);
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    console.error('TextFX raylight program error:', gl.getProgramInfoLog(p));
    gl.deleteProgram(p);
    return null;
  }
  return p;
}
